'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useSyncExternalStore } from 'react';
import { CLERK_SESSION_REFRESH_TIMING } from '@lib/auth/constants/clerk-session-refresh-timing';
import { observeClerkRefreshIndicatorShown } from '@lib/observability/clerk-refresh-tags/observe-clerk-refresh.client';
import { type ClerkRefreshIndicatorState, addClerkRefreshStateListener, getClerkRefreshState } from '../clerk-session-bridge';
const {
  showRefreshingAfterMs,
  autoClearFailedAfterMs
} = CLERK_SESSION_REFRESH_TIMING.indicator;
export type ClerkRefreshIndicatorView = {
  kind: 'idle';
} | {
  kind: 'refreshing';
} | {
  kind: 'failed';
};
function subscribe(callback: () => void): () => void {
  return addClerkRefreshStateListener(callback);
}
function getServerSnapshot(): ClerkRefreshIndicatorState {
  return 'idle';
}

/**
 * Subscribes to {@link clerk-session-bridge}'s refresh indicator state and
 * returns a debounced "what should the UI show right now" view.
 *
 * - `refreshing` is only surfaced after the refresh has been in flight
 *   for `CLERK_SESSION_REFRESH_TIMING.indicator.showRefreshingAfterMs`.
 *   Fast refreshes (the common case on every visibilitychange) never
 *   reach the UI.
 * - `failed` surfaces immediately so the user gets feedback on a real
 *   problem, then auto-clears after
 *   `CLERK_SESSION_REFRESH_TIMING.indicator.autoClearFailedAfterMs` so a
 *   one-off blip does not leave the banner stuck.
 */
export function useClerkRefreshIndicator() {
  const $ = _c(9);
  const state = useSyncExternalStore(subscribe, getClerkRefreshState, getServerSnapshot);
  const [showRefreshing, setShowRefreshing] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  let t0;
  let t1;
  if ($[0] !== state) {
    t0 = () => {
      if (state !== "refreshing") {
        setShowRefreshing(false);
        return;
      }
      const timer = setTimeout(() => {
        setShowRefreshing(true);
        observeClerkRefreshIndicatorShown("refreshing");
      }, showRefreshingAfterMs);
      return () => clearTimeout(timer);
    };
    t1 = [state];
    $[0] = state;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== state) {
    t2 = () => {
      if (state !== "failed") {
        setShowFailed(false);
        return;
      }
      setShowFailed(true);
      observeClerkRefreshIndicatorShown("failed");
      const timer_0 = setTimeout(() => setShowFailed(false), autoClearFailedAfterMs);
      return () => clearTimeout(timer_0);
    };
    t3 = [state];
    $[3] = state;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (showFailed) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        kind: "failed"
      };
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  if (showRefreshing) {
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        kind: "refreshing"
      };
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      kind: "idle"
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}